// Leaflet map for the New Season editors: block polygons with name labels, the
// selected block highlighted, sensor pins, home / + / − controls, "Search by
// name or coordinates" + Navigate, and a layers toggle. With `showDrawTools`,
// production Edit Field's leaflet.pm toolbar (CustomMap's showEditToolBar):
// draw polygon, edit (every polygon's vertices), and remove (click a polygon).
//
// Built separately from CustomMap (which is left untouched) because CustomMap is
// a dashboard map bound to the V1 field model.
const React = require('react');
const { useEffect, useRef, useState, useCallback } = require('react');
const { useTranslation } = require('react-i18next');
const L = require('leaflet');
require('leaflet.pm');
require('leaflet.pm/dist/leaflet.pm.css');
require('leaflet/dist/leaflet.css');
const { createGoogleTileLayer } = require('../../utils/googleMapTiles');
const { parseCoordinates } = require('./layoutHelpers');

const DEFAULT_CENTER = [32.095998, 34.776344];
const DEFAULT_ZOOM = 15;
const NAVIGATE_ZOOM = 17;
const FIT_PADDING = [32, 32];
const FOCUS_MAX_ZOOM = 18;

const ringToLatLngs = (ring) =>
  (ring || []).map((point) => [point.latitude, point.longitude]);

const latLngsToRing = (latLngs) =>
  latLngs.map((latLng) => ({ latitude: latLng.lat, longitude: latLng.lng }));

const polygonClassName = (isSelected) =>
  'ns-poly' + (isSelected ? ' ns-poly-selected' : '');

const firstRing = (layer) => {
  const rings = layer.getLatLngs();
  return latLngsToRing(Array.isArray(rings[0]) ? rings[0] : rings);
};

// CustomMap's toolbar and polygon drawing options (showEditToolBar).
const EDIT_TOOLBAR_OPTIONS = {
  position: 'topleft',
  useFontAwesome: false,
  drawMarker: false,
  drawPolyline: false,
  drawRectangle: false,
  drawPolygon: true,
  drawCircle: false,
  cutPolygon: false,
  editMode: true,
  removalMode: true,
};

const DRAW_POLYGON_OPTIONS = {
  snappable: true,
  snapDistance: 10,
  templineStyle: { color: '#FFFFFF', weight: 4 },
  hintlineStyle: { color: '#FFFFFF', dashArray: [5, 5] },
  pathOptions: { color: '#FFFFFF', fillOpacity: 0.5 },
};

const showEditToolbar = (map) => {
  // As CustomMap: set the polygon options by entering and leaving the mode.
  map.pm.enableDraw('Poly', DRAW_POLYGON_OPTIONS);
  map.pm.disableDraw('Poly');
  map.pm.addControls(EDIT_TOOLBAR_OPTIONS);
};

const hideEditToolbar = (map) => {
  map.pm.disableDraw('Poly');
  if (map.pm.globalEditEnabled()) {
    map.pm.disableGlobalEditMode();
  }
  if (map.pm.globalRemovalEnabled()) {
    map.pm.toggleGlobalRemovalMode();
  }
  map.pm.removeControls();
};

const createMap = (element) => {
  const satellite = createGoogleTileLayer('hybrid');
  const streets = createGoogleTileLayer('roadmap');
  const map = L.map(element, {
    center: DEFAULT_CENTER,
    zoom: DEFAULT_ZOOM,
    zoomControl: false,
    layers: [satellite],
    attributionControl: true,
    fadeAnimation: false,
  });
  map.attributionControl.setPrefix('');
  return { map, baseLayers: { satellite, streets } };
};

// Blocks are redrawn on every change, so a polygon added while the toolbar's
// edit or remove mode is on joins that mode here. Removal is handled by the
// polygon's own click (leaflet.pm fires pm:remove for any layer removal, our
// redraws included, so it cannot be listened to).
const addBlockLayers = (group, block, options) => {
  const { map, isSelected, onClick, onAreaEdited, onAreaRemoved } = options;
  (block.areas || []).forEach((ring, index) => {
    const polygon = L.polygon(ringToLatLngs(ring), {
      className: polygonClassName(isSelected),
    });
    if (index === 0 && block.name) {
      polygon.bindTooltip(block.name, {
        permanent: true,
        direction: 'center',
        className:
          'ns-map-label' + (isSelected ? ' ns-map-label-selected' : ''),
      });
    }
    polygon.on('click', (event) => {
      if (map.pm.globalRemovalEnabled()) {
        onAreaRemoved(block.key, index);
      } else {
        onClick(block.key, event.originalEvent);
      }
    });
    polygon.on('pm:edit', () =>
      onAreaEdited(block.key, index, firstRing(polygon)),
    );
    group.addLayer(polygon);
    if (map.pm.globalEditEnabled()) {
      polygon.pm.enable();
    }
  });
};

const addSensorMarkers = (group, markers) => {
  (markers || []).forEach((marker) => {
    const icon = L.divIcon({
      className:
        'ns-sensor-pin' + (marker.selected ? ' ns-sensor-pin-selected' : ''),
      iconSize: [14, 14],
    });
    const layer = L.marker([marker.lat, marker.lng], {
      icon,
      title: marker.title,
    });
    if (marker.onClick) {
      layer.on('click', marker.onClick);
    }
    group.addLayer(layer);
  });
};

const findBlockByName = (blocks, query) => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return null;
  }
  return (
    blocks.find((block) => block.name.toLowerCase() === needle) ||
    blocks.find((block) => block.name.toLowerCase().includes(needle)) ||
    null
  );
};

const blockBounds = (block) =>
  L.latLngBounds(
    (block.areas || []).reduce(
      (points, ring) => points.concat(ringToLatLngs(ring)),
      [],
    ),
  );

const MapButton = ({ title, onClick, children, disabled, active }) => (
  <button
    type='button'
    className={'ns-map-btn' + (active ? ' ns-map-btn-active' : '')}
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

const MapSearch = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [notFound, setNotFound] = useState(false);
  const submit = (event) => {
    event.preventDefault();
    setNotFound(!onNavigate(query));
  };
  return (
    <form className='ns-map-search' onSubmit={submit}>
      <input
        className='form-control ns-input'
        value={query}
        placeholder={t('new_season_search_map')}
        onChange={(event) => {
          setQuery(event.target.value);
          setNotFound(false);
        }}
      />
      <button type='submit' className='ns-btn ns-btn-primary ns-btn-small'>
        {t('new_season_navigate')}
      </button>
      {notFound && (
        <div className='ns-map-search-error'>
          {t('new_season_not_found', { query })}
        </div>
      )}
    </form>
  );
};

const useLatest = (value) => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

const hasSize = (map) => {
  const size = map.getSize();
  return size.x > 0 && size.y > 0;
};

// Creates the map once. A ResizeObserver keeps Leaflet's size in sync with the
// container: a map measured at 0×0 (laid out after creation) would otherwise
// fit to minimum zoom and clip its vector layers, leaving only the labels.
const useLeafletMap = (containerRef, onDrawnRef, onResizeRef) => {
  const stateRef = useRef(null);
  useEffect(() => {
    const { map, baseLayers } = createMap(containerRef.current);
    const blocksGroup = L.featureGroup().addTo(map);
    const markersGroup = L.featureGroup().addTo(map);
    map.on('pm:create', (event) => {
      const areas = [firstRing(event.layer)];
      map.removeLayer(event.layer);
      onDrawnRef.current?.(areas);
    });
    stateRef.current = {
      map,
      baseLayers,
      blocksGroup,
      markersGroup,
      fitted: false,
    };
    const onResize = () => {
      map.invalidateSize({ pan: false });
      onResizeRef.current?.();
    };
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(onResize)
        : null;
    observer?.observe(containerRef.current);
    const resizeTimer = setTimeout(onResize, 0);
    return () => {
      clearTimeout(resizeTimer);
      observer?.disconnect();
      map.remove();
      stateRef.current = null;
    };
  }, [containerRef, onDrawnRef, onResizeRef]);
  return stateRef;
};

const boundsOfBlocks = (blocks, keys) => {
  const wanted = new Set(keys);
  const bounds = L.latLngBounds([]);
  (blocks || [])
    .filter((block) => wanted.has(block.key))
    .forEach((block) => bounds.extend(blockBounds(block)));
  return bounds;
};

const BlocksMap = ({
  blocks,
  selectedKeys,
  onBlockClick,
  onDrawn,
  onAreaEdited,
  onAreaRemoved,
  markers,
  showDrawTools,
  className,
  focusKeys,
  fitKey,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const onDrawnRef = useLatest(onDrawn);
  const onBlockClickRef = useLatest(onBlockClick);
  const onAreaEditedRef = useLatest(onAreaEdited);
  const onAreaRemovedRef = useLatest(onAreaRemoved);
  const blocksRef = useLatest(blocks);
  const focusKey = (focusKeys || []).join('|');
  const focusKeyRef = useLatest(focusKey);
  const onResizeRef = useRef(null);
  const leafletRef = useLeafletMap(containerRef, onDrawnRef, onResizeRef);
  const [baseLayer, setBaseLayer] = useState('satellite');

  const fitAll = useCallback(() => {
    const leaflet = leafletRef.current;
    const bounds = leaflet?.blocksGroup.getBounds();
    if (bounds?.isValid() && hasSize(leaflet.map)) {
      leaflet.map.fitBounds(bounds, { padding: FIT_PADDING });
      return true;
    }
    return false;
  }, [leafletRef]);

  // Focused blocks (Season editor: the selected block) if any, else all blocks.
  const fitView = useCallback(() => {
    const leaflet = leafletRef.current;
    const keys = focusKeyRef.current ? focusKeyRef.current.split('|') : [];
    const bounds = keys.length ? boundsOfBlocks(blocksRef.current, keys) : null;
    if (leaflet && bounds?.isValid() && hasSize(leaflet.map)) {
      leaflet.map.fitBounds(bounds, {
        padding: FIT_PADDING,
        maxZoom: FOCUS_MAX_ZOOM,
      });
      return true;
    }
    return fitAll();
  }, [leafletRef, focusKeyRef, blocksRef, fitAll]);

  onResizeRef.current = () => {
    const leaflet = leafletRef.current;
    if (leaflet && !leaflet.fitted) {
      leaflet.fitted = fitView();
    }
  };

  useEffect(() => {
    const leaflet = leafletRef.current;
    if (!leaflet) {
      return;
    }
    const selected = new Set(selectedKeys || []);
    leaflet.blocksGroup.clearLayers();
    (blocks || []).forEach((block) =>
      addBlockLayers(leaflet.blocksGroup, block, {
        map: leaflet.map,
        isSelected: selected.has(block.key),
        onClick: (key, event) => onBlockClickRef.current?.(key, event),
        onAreaEdited: (key, index, ring) =>
          onAreaEditedRef.current?.(key, index, ring),
        onAreaRemoved: (key, index) => onAreaRemovedRef.current?.(key, index),
      }),
    );
    if (!leaflet.fitted) {
      leaflet.fitted = fitView();
    }
  }, [
    blocks,
    selectedKeys,
    leafletRef,
    fitView,
    onBlockClickRef,
    onAreaEditedRef,
    onAreaRemovedRef,
  ]);

  // Re-fit when the farming unit (fitKey) or the focused blocks change.
  useEffect(() => {
    const leaflet = leafletRef.current;
    if (leaflet) {
      leaflet.fitted = fitView();
    }
  }, [focusKey, fitKey, leafletRef, fitView]);

  useEffect(() => {
    const leaflet = leafletRef.current;
    if (!leaflet) {
      return;
    }
    leaflet.markersGroup.clearLayers();
    addSensorMarkers(leaflet.markersGroup, markers);
  }, [markers, leafletRef]);

  useEffect(() => {
    const map = leafletRef.current?.map;
    if (!map || !showDrawTools) {
      return undefined;
    }
    showEditToolbar(map);
    return () => hideEditToolbar(map);
  }, [showDrawTools, leafletRef]);

  useEffect(() => {
    const leaflet = leafletRef.current;
    if (!leaflet) {
      return;
    }
    const { map, baseLayers } = leaflet;
    Object.keys(baseLayers).forEach((name) => {
      if (name !== baseLayer && map.hasLayer(baseLayers[name])) {
        map.removeLayer(baseLayers[name]);
      }
    });
    if (!map.hasLayer(baseLayers[baseLayer])) {
      baseLayers[baseLayer].addTo(map);
    }
  }, [baseLayer, leafletRef]);

  const navigateTo = useCallback(
    (query) => {
      const map = leafletRef.current?.map;
      const coordinates = parseCoordinates(query);
      if (map && coordinates) {
        map.setView([coordinates.lat, coordinates.lng], NAVIGATE_ZOOM);
        return true;
      }
      const block = findBlockByName(blocksRef.current || [], query);
      const bounds = block && blockBounds(block);
      if (map && bounds?.isValid()) {
        map.fitBounds(bounds, { padding: FIT_PADDING });
        return true;
      }
      return false;
    },
    [leafletRef, blocksRef],
  );

  const zoom = (delta) =>
    leafletRef.current?.map.setZoom(leafletRef.current.map.getZoom() + delta);

  return (
    <div className={'ns-map-wrapper ' + (className || '')}>
      <div ref={containerRef} className='ns-map' />
      <div className='ns-map-zoom'>
        <MapButton title={t('new_season_home')} onClick={fitAll}>
          ⌂
        </MapButton>
        <MapButton title={t('new_season_zoom_in')} onClick={() => zoom(1)}>
          +
        </MapButton>
        <MapButton title={t('new_season_zoom_out')} onClick={() => zoom(-1)}>
          −
        </MapButton>
      </div>
      <MapSearch onNavigate={navigateTo} />
      <div className='ns-map-layers'>
        <MapButton
          title={t('new_season_layers')}
          onClick={() =>
            setBaseLayer((current) =>
              current === 'satellite' ? 'streets' : 'satellite',
            )
          }
        >
          {baseLayer === 'satellite'
            ? t('new_season_satellite')
            : t('new_season_streets')}
        </MapButton>
      </div>
    </div>
  );
};

module.exports = BlocksMap;
